import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Skeleton,
  Button,
  Stack,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import {
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import MaterialProductCard from "./MaterialProductCard";
import { get_material_store, get_state, get_city } from "../../api/ControllApi";
import MaterialStoreFilter from "./MaterialStoreFilter";
import { useNavigate, useParams, useLocation , useSearchParams  } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import AboutShop from "./AboutShop";
import ReviewRating from "./ReviewRating";

// Animation Library
import { motion, AnimatePresence } from "framer-motion";

export default function MaterialStoreShop() {
  const [materialList, setMaterialList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [searchParams] = useSearchParams();

const materialName = searchParams.get("material_name");
  const { id  } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stateId = queryParams.get("state");
  const cityId = queryParams.get("city");
  const ratingVal = queryParams.get("rating");



  const [filters, setFilters] = useState({
    state: stateId || "",
    city: cityId || "",
    rating: ratingVal || "",
  });
  const [page, setPage] = useState(1);
  const [viewType, setViewType] = useState("grid");
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopDialogOpen, setShopDialogOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    get_state().then((res) => setStates(res?.data || []));
  }, []);

  useEffect(() => {
    if (filters.state) {
      get_city({ state_id: filters.state }).then((res) => setCities(res?.data || []));
    } else {
      setCities([]);
    }
  }, [filters.state]);

  const fetchGeoPlots = async (filters = {}, page = 1) => {
    setLoading(true);

    try {
      const params = {
        product_id: id,
        state: filters.state,
        city: filters.city,
        rating: filters.rating,
        name: filters.searchQuery,
        per_unit: 12,
      };

      const res = await get_material_store(params);

      setMaterialList(res?.data?.material || res?.data || []);
      setPagination(res?.data?.pagination || res?.pagination || { current_page: 1, last_page: 1 });
    } catch (err) {

      setMaterialList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (materialName) {
      setFilters(prev => ({
        ...prev,
        material_name: materialName
      }));
      setSelectedMaterial(materialName);
    }
  }, [materialName]);



  useEffect(() => {
    fetchGeoPlots(filters, page);
  }, [filters, page]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Logic to handle view switch
  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setViewType(nextView);
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: { xs: '95%', md: '80%' }, mx: 'auto', py: 5 }}>
      {/* HEADER */}
      <Box
        sx={{
          mb: 3,
          mt: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BreadcrumbsNav />
          <Box sx={{ paddingLeft: 12, display: "flex", justifyContent: "flex-end" }}>
  <Typography fontSize={{ xs: 16, sm: 18, md: 20 }}>
    Top{" "}
    <Box component="span" sx={{ color: "#95B160", fontWeight: 600 }}>
      {selectedMaterial || "Shops"}
    </Box>{" "}
    Near By You
  </Typography>
</Box>
        </Box>

        {/* VIEW SWITCHER LOGIC */}
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          aria-label="view switcher"
          size="small"
          sx={{
            backgroundColor: "#fff",
            "& .Mui-selected": {
              backgroundColor: "#eaede3 !important",
              color: "#95B160 !important",
            },
          }}
        >
          <ToggleButton value="grid" aria-label="grid view">
            <Tooltip title="Grid View">
              <GridViewIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <Tooltip title="List View">
              <ListViewIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>


      {/* LAYOUT */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
          gap: 6,
        }}
      >
        <Box>
          <MaterialStoreFilter
            onFilterChange={handleFilterChange}
            selectedState={filters.state}
            selectedCity={filters.city}
            selectedRating={filters.rating}
          />
        </Box>


        {/* CARD LIST */}
        <Box>

          <motion.div
            layout // Framer motion animates the layout shift
            style={{
              display: "grid",
              gap: "24px",
              // Dynamically change grid columns based on viewType
              gridTemplateColumns:
                viewType === "list"
                  ? "1fr"
                  : "repeat(auto-fill, minmax(300px, 1fr))",
            }}
          >
            <AnimatePresence mode="popLayout">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <motion.div
                    key={`skeleton-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={viewType === "list" ? 180 : 320}
                      sx={{ borderRadius: 3 }}
                    />
                  </motion.div>
                ))
                : materialList.length > 0 ? materialList.map((card) => {
                  const imageUrl = Array.isArray(card.image)
                    ? card.image[0]
                    : card.image;

                  return (
                    <motion.div
                      key={card.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MaterialProductCard
                        key={card.id}
                        id={card.id}
                        userid={card.user_id}
                        name={card.name}
                        images={card.thumbnail}
                        phone={card.phone}
                        email={card.email}
                        gst={card.gst}
                        location={card.address}
                        website={card.website}
                        rating={(Number(card.reviews_avg_rating || 0)).toFixed(1)}
                        reviewCount={card.reviews_count || 0}
                        established={card?.estimate_date}
                        description={card.description}
                        whatsapp={card?.whatsapp}
                        viewType={viewType} // Passing viewType to card for internal layout changes
                        onShopView={() => {
                          const queryParams = (materialName && id) ? `?materialName=${encodeURIComponent(materialName)}&materialId=${id}` : '';
                          navigate(`/material-store/detail/${card.user_id}${queryParams}`);
                        }}
                      />
                    </motion.div>
                  );
                }) : (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "40vh",
                      textAlign: "center",
                      width: "100%",
                      py: 5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/images/nodatafound.png"
                      alt="No Data Found"
                      sx={{ width: { xs: 280, sm: 320, md: 350 }, mb: 2 }}
                    />
                  </Box>
                )
              }
            </AnimatePresence>
          </motion.div>
        </Box>
      </Box>

      {/* Shop Details Dialog */}
      <Dialog
        open={shopDialogOpen}
        onClose={() => setShopDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedShop?.name || "Shop Details"}
          <IconButton onClick={() => setShopDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedShop && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <AboutShop
                description={selectedShop.description}
                taxnumber={selectedShop.gst}
                established={selectedShop.estimate_date}
              />
              <ReviewRating
                shopId={selectedShop.id}
                rating={selectedShop.reviews_avg_rating}
                count={selectedShop.reviews_count}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 8,
          mb: 4,
        }}
      >
        {pagination.last_page > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(
              (p) => (
                <Button
                  key={p}
                  variant={p === page ? "contained" : "outlined"}
                  onClick={() => handlePageChange(p)}
                  sx={{
                    borderRadius: "9px",
                    bgcolor: p === page ? "#95B160" : "transparent",
                    color: p === page ? "#fff" : "#95B160",
                    borderColor: "#95B160",
                    minWidth: 40,
                    height: 40,
                    "&:hover": {
                      bgcolor: p === page ? "#7a944d" : "rgba(149, 177, 96, 0.1)",
                      borderColor: "#95B160",
                    },
                  }}
                >
                  {p}
                </Button>
              )
            )}
          </Stack>
        )}
      </Box>
    </Container>
  );
}